import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { useCreateOrganization, useSalesReps } from '@/services/queries';
import { ApiError } from '@/services/api/client';
import { useAppSelector } from '@/store/hooks';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { cn } from '@/shared/lib/utils';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import {
  DIALOG_BUTTON_OUTLINE,
  DIALOG_BUTTON_PRIMARY,
  DIALOG_CLOSE,
  FIELD_CONTROL,
} from './field-styles';
import { Field, FieldInput, OptionCard } from './fields';

/**
 * Creating an organization - one page, one field group, nothing else.
 *
 * There used to be a licence and a set of named people collected in the same
 * flow, over three steps. There no longer is: this creates the organization
 * row alone, which is enough for it to appear in the table - "not activated",
 * the same state a lab already shows before its first seat binds - and a
 * licence is issued to it separately, later, once the deal is ready. No
 * wizard, no step to skip, and nothing generated here that would need a
 * "shown once" screen the way a password does.
 */

interface FormValues {
  name: string;
  orgType: 'lab' | 'individual_dentist';
  primaryContactName: string;
  primaryEmail: string;
  phone: string;
  gstNumber: string;
  address: string;
  salesRepId: string;
  notes: string;
}

const initialValues: FormValues = {
  name: '',
  orgType: 'lab',
  primaryContactName: '',
  primaryEmail: '',
  phone: '',
  gstNumber: '',
  address: '',
  salesRepId: '',
  notes: '',
};

const schema = Yup.object({
  name: Yup.string().trim().required('Enter the organization name.').max(200),
  orgType: Yup.string().oneOf(['lab', 'individual_dentist']).required(),
  primaryContactName: Yup.string().trim().max(200),
  primaryEmail: Yup.string()
    .trim()
    .required('Enter a billing email address.')
    .email('Enter a valid email address.')
    .max(320),
  phone: Yup.string().trim().max(40),
  gstNumber: Yup.string().trim().max(40),
  address: Yup.string().trim().max(500),
  // Empty means "not assigned yet" - a blank organization is not an error,
  // and defaulting it to the signed-in operator (below) means this is
  // usually filled before anyone notices it could be empty.
  salesRepId: Yup.string(),
  notes: Yup.string().trim().max(2000),
});

export function CreateOrganizationDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        660px and unpadded, as the design draws it.

        `p-0` and `gap-0` because the header, body and footer each carry their
        own padding and their own full-bleed rule - a single `p-6` on the
        content would inset those rules from the edges, which is the detail
        that makes a dialog look like a box with lines in it rather than one
        with a header and a footer.

        Still a flex column capped to the viewport rather than a fixed height,
        so a long address or notes value cannot push the buttons out of view.
      */}
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-165"
      >
        <DialogBody onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

function DialogBody({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const createOrganization = useCreateOrganization();
  const salesReps = useSalesReps();
  const currentUserId = useAppSelector((state) => state.auth.user?.id);

  /*
   * Defaults the picker to the signed-in operator via `initialValues` rather
   * than an effect that calls `setFieldValue` after mount: the id is already
   * in Redux - synchronously, no request to wait for - by the time this
   * dialog can open at all, so there is no later moment to catch. Setting it
   * post-mount instead raced Formik's own validation cycle under React 18
   * Strict Mode's double-effect invocation and lost the value back to blank.
   */
  const formik = useFormik<FormValues>({
    initialValues: { ...initialValues, salesRepId: currentUserId ?? '' },
    validationSchema: schema,
    onSubmit: (values, helpers) => {
      createOrganization.mutate(
        {
          name: values.name.trim(),
          orgType: values.orgType,
          primaryEmail: values.primaryEmail.trim(),
          // Omitted rather than sent empty: the server's schema treats an
          // absent optional and an empty string differently.
          ...(values.primaryContactName.trim()
            ? { primaryContactName: values.primaryContactName.trim() }
            : {}),
          ...(values.phone.trim() ? { phone: values.phone.trim() } : {}),
          ...(values.gstNumber.trim() ? { gstNumber: values.gstNumber.trim() } : {}),
          ...(values.address.trim() ? { address: values.address.trim() } : {}),
          ...(values.salesRepId ? { salesRepId: values.salesRepId } : {}),
          ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        },
        {
          onSuccess: (created) => {
            toast.success(`${created.name} created`, {
              description: 'Shows as not activated until a licence is issued.',
            });
            onOpenChange(false);
          },
          onError: (error) => {
            if (error instanceof ApiError && error.fieldErrors) {
              const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), values);
              helpers.setErrors(placed);
            }
          },
        },
      );
    },
  });

  const apiError = createOrganization.error instanceof ApiError ? createOrganization.error : null;

  /** Shown once touched, so the form does not scold somebody mid-typing. */
  const error = (field: keyof FormValues): string | undefined =>
    formik.touched[field] ? formik.errors[field] : undefined;

  const banner = (): string | null => {
    if (!apiError) return null;
    if (!apiError.fieldErrors) return apiError.message;
    const { unplaced } = splitFormErrors(toFormErrors(apiError.fieldErrors), formik.values);
    return unplaced.length > 0 ? apiError.message : null;
  };

  return (
    <>
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-muted px-6 py-5">
        <div className="min-w-0">
          <DialogTitle className="text-[16.5px] font-semibold tracking-[-0.2px]">
            New organization
          </DialogTitle>
          <DialogDescription className="mt-0.75 text-[12px] text-neutral-400">
            Creates the organization on its own — issue a licence to it separately once the deal is
            ready.
          </DialogDescription>
        </div>

        {/* The design's own close affordance, which is why DialogContent's
            stock floating glyph is turned off above. */}
        <DialogClose className={DIALOG_CLOSE} aria-label="Close">
          <X size={14} strokeWidth={2.4} aria-hidden="true" />
        </DialogClose>
      </div>

      <form onSubmit={formik.handleSubmit} className="flex min-h-0 flex-1 flex-col" noValidate>
        {/*
          `-mx-1 px-1` inside the section padding rather than `pr-1`: a focus
          ring is drawn 3px OUTSIDE the input it belongs to, and
          `overflow-y-auto` forces overflow-x to clip as well, so a field
          flush with this container's edge had a third of its ring sliced off.
        */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div className="-mx-1 grid grid-cols-1 gap-3.5 px-1 sm:grid-cols-2">
            {banner() && (
              <p
                role="alert"
                className="rounded-[9px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive sm:col-span-2"
              >
                {banner()}
              </p>
            )}

            <Field label="Organization name" htmlFor="org-name" error={error('name')} wide>
              <FieldInput
                id="org-name"
                {...formik.getFieldProps('name')}
                aria-invalid={Boolean(error('name'))}
              />
            </Field>

            <Field
              label="Org type"
              wide
              hint="Recorded on the organization — it does not restrict which licence you issue to it later."
            >
              <div className="grid grid-cols-2 gap-2">
                <OptionCard
                  title="Individual Dentist"
                  subtitle="One seat, one workstation"
                  selected={formik.values.orgType === 'individual_dentist'}
                  onSelect={() => {
                    void formik.setFieldValue('orgType', 'individual_dentist');
                  }}
                />
                <OptionCard
                  title="Lab"
                  subtitle="Multiple seats, one org admin"
                  selected={formik.values.orgType === 'lab'}
                  onSelect={() => {
                    void formik.setFieldValue('orgType', 'lab');
                  }}
                />
              </div>
            </Field>

            <Field label="Primary contact" htmlFor="org-contact">
              <FieldInput id="org-contact" {...formik.getFieldProps('primaryContactName')} />
            </Field>

            <Field label="Primary email" htmlFor="org-email" error={error('primaryEmail')}>
              <FieldInput
                id="org-email"
                type="email"
                mono
                {...formik.getFieldProps('primaryEmail')}
                aria-invalid={Boolean(error('primaryEmail'))}
              />
            </Field>

            <Field label="Phone" htmlFor="org-phone">
              <FieldInput id="org-phone" mono {...formik.getFieldProps('phone')} />
            </Field>

            <Field label="GST / VAT number" htmlFor="org-gst">
              <FieldInput id="org-gst" mono {...formik.getFieldProps('gstNumber')} />
            </Field>

            <Field label="Address" htmlFor="org-address" wide>
              <FieldInput id="org-address" {...formik.getFieldProps('address')} />
            </Field>

            <Field label="Sales rep" htmlFor="org-sales-rep">
              <Select
                value={formik.values.salesRepId}
                onValueChange={(value) => {
                  void formik.setFieldValue('salesRepId', value);
                }}
              >
                {/* `data-[size=default]:h-9` on the shared trigger is a
                    variant, so a plain `h-9.5` loses to it - the override has
                    to carry the same variant to win. */}
                <SelectTrigger
                  id="org-sales-rep"
                  className={cn(FIELD_CONTROL, 'data-[size=default]:h-9.5')}
                >
                  <SelectValue placeholder={salesReps.isPending ? 'Loading…' : 'Unassigned'} />
                </SelectTrigger>
                <SelectContent>
                  {salesReps.data?.map((rep) => (
                    <SelectItem key={rep.id} value={rep.id}>
                      {rep.name}
                      {rep.id === currentUserId ? ' (me)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Notes" htmlFor="org-notes">
              <FieldInput
                id="org-notes"
                placeholder="Optional — internal only"
                {...formik.getFieldProps('notes')}
              />
            </Field>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2.25 border-t border-border bg-muted/20 px-6 py-3.5">
          <button
            type="button"
            className={DIALOG_BUTTON_OUTLINE}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={DIALOG_BUTTON_PRIMARY}
            disabled={createOrganization.isPending}
          >
            {createOrganization.isPending ? 'Creating…' : 'Create organization'}
          </button>
        </div>
      </form>
    </>
  );
}
